import { usePreviewRunServicesQuery } from "@/api/previewInsights";
import { EM_DASH } from "@/lib/format";
import type { LabRunModel } from "@/lib/labRunModel";
import { featureEntries, serviceFlagEntries } from "@/lib/labServices";
import { CAPTURE_STATE_LABELS } from "@/lib/previewInsights";
import { Badge } from "@/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { EmptyState } from "@/ui/states";

import { LabFeatureGroups } from "../LabFeatureGroups";
import { LabExportPanel } from "../LabExportControls";
import { LabIdentifiers } from "../LabExportIdentifiers";

/**
 * Run overview: the run's own figures, how complete the captured trace is, and
 * the runtime identity that served it. Everything is read from the model; the
 * component only formats. A trace that hit the capture cap is announced above
 * everything else, because every other number below it is then a lower bound.
 */

const SIZE_CAP_NOTICE =
  "Trace capture cap reached (size_cap_exceeded): later events are missing, this run is not complete";

type Props = {
  projectId: string;
  model: LabRunModel;
  selectedTurn: number | null;
  onSelectTurn: (turn: number) => void;
};

function shortRef(ref: string | null): string {
  if (ref === null) return "event not named";
  return ref.length > 16 ? `${ref.slice(0, 12)}…` : ref;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{value}</dd>
    </div>
  );
}

function orNone(value: number | null): string {
  return value === null ? "none recorded" : String(value);
}

export function LabOverviewTab({ projectId, model }: Props) {
  const o = model.overview;
  const services = usePreviewRunServicesQuery(
    projectId,
    model.previewRunRef || null,
  );
  const features = featureEntries(services.data);
  const serviceFlags = serviceFlagEntries(services.data);
  const captureLabel =
    (CAPTURE_STATE_LABELS as Record<string, string>)[o.captureState] ??
    o.captureState;
  const policyOmissions = o.omissions.filter((omission) => omission.restricted);
  const lossOmissions = o.omissions.filter((omission) => !omission.restricted);

  return (
    <section aria-labelledby="lab-overview-heading" className="space-y-4">
      <h2
        id="lab-overview-heading"
        className="text-base font-semibold text-foreground"
      >
        Overview
      </h2>

      {o.sizeCapExceeded ? (
        <div
          role="alert"
          className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm font-medium text-destructive"
        >
          <p>{SIZE_CAP_NOTICE}</p>
          {o.captureWarning !== null ? (
            <p className="mt-1 font-normal">{o.captureWarning}</p>
          ) : null}
        </div>
      ) : o.captureWarning !== null ? (
        <p
          role="status"
          className="rounded-md bg-warning p-3 text-sm text-warning-foreground"
        >
          {o.captureWarning}
        </p>
      ) : null}

      {model.lab.turns.length === 0 ? (
        <EmptyState
          title="No turns recorded yet"
          description="The run has not captured a turn, so there is nothing to summarise beyond its state."
        />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Run</CardTitle>
        </CardHeader>
        <CardBody>
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat label="State" value={o.stateLabel || EM_DASH} />
            <Stat label="Capture" value={captureLabel || EM_DASH} />
            <Stat label="Turns" value={String(o.turnCount)} />
            <Stat label="Events" value={orNone(o.eventCount)} />
            <Stat label="Last sequence" value={orNone(o.lastSequence)} />
            {o.simTotals !== null ? (
              <>
                <Stat
                  label="Simulated turns"
                  value={String(o.simTotals.turns)}
                />
                <Stat
                  label="Mean simulator latency"
                  value={
                    o.simTotals.meanLatencyMs === null
                      ? "none recorded"
                      : `${o.simTotals.meanLatencyMs} ms`
                  }
                />
                <Stat
                  label="Simulator models"
                  value={
                    o.simTotals.models.length > 0
                      ? o.simTotals.models.join(", ")
                      : "none recorded"
                  }
                />
              </>
            ) : null}
          </dl>
          {features.length > 0 || serviceFlags.length > 0 ? (
            <div className="mt-3">
              <p className="text-xs font-medium text-muted-foreground">
                Features this run used
              </p>
              <LabFeatureGroups
                features={features}
                serviceFlags={serviceFlags}
              />
            </div>
          ) : null}
        </CardBody>
      </Card>

      <LabIdentifiers projectId={projectId} model={model} />

      <Card>
        <CardHeader>
          <CardTitle>Export</CardTitle>
        </CardHeader>
        <CardBody>
          <LabExportPanel projectId={projectId} model={model} ready />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Trace completeness</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-foreground">
            Complete through sequence:{" "}
            <span dir="ltr" className="font-mono">
              {orNone(o.completeThroughSequence)}
            </span>
          </p>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Missing ranges
            </h3>
            {o.missingRanges.length === 0 ? (
              <p className="text-sm text-muted-foreground">None recorded</p>
            ) : (
              <ul className="space-y-1">
                {o.missingRanges.map((range, index) => (
                  <li key={index} className="text-sm text-foreground">
                    <Badge tone="destructive" className="me-2">
                      <span dir="ltr">
                        {range.from ?? "?"}–{range.to ?? "?"}
                      </span>
                    </Badge>
                    {range.sentence}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Omissions
            </h3>
            {lossOmissions.length === 0 ? (
              <p className="text-sm text-muted-foreground">None recorded</p>
            ) : (
              <ul className="space-y-1">
                {lossOmissions.map((omission, index) => (
                  <li
                    key={index}
                    className="flex flex-wrap items-center gap-2 text-sm text-foreground"
                  >
                    <span
                      dir="ltr"
                      className="font-mono text-xs"
                      title={omission.eventRef ?? undefined}
                    >
                      {shortRef(omission.eventRef)}
                    </span>
                    <span
                      dir="ltr"
                      className="font-mono text-xs text-muted-foreground"
                    >
                      {omission.field ?? "field not named"}
                    </span>
                    <Badge
                      tone={
                        omission.sizeCapExceeded ? "destructive" : "warning"
                      }
                    >
                      {omission.reasonLabel}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Withheld by policy
            </h3>
            {policyOmissions.length === 0 ? (
              <p className="text-sm text-muted-foreground">None recorded</p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  A policy outcome, not lost data.
                </p>
                <p className="text-sm text-foreground">
                  {policyOmissions.length}{" "}
                  {policyOmissions.length === 1 ? "field" : "fields"} withheld (
                  <span dir="ltr" className="font-mono text-xs">
                    {[
                      ...new Set(
                        policyOmissions.map(
                          (o) => o.field ?? "field not named",
                        ),
                      ),
                    ].join(", ")}
                  </span>
                  )
                </p>
                <details className="text-sm">
                  <summary className="cursor-pointer text-xs text-muted-foreground focus-visible:outline-2 focus-visible:outline-ring">
                    Show each withheld field
                  </summary>
                  <ul className="space-y-1">
                    {policyOmissions.map((omission, index) => (
                      <li
                        key={index}
                        className="flex flex-wrap items-center gap-2 text-sm text-foreground"
                      >
                        <span
                          dir="ltr"
                          className="font-mono text-xs"
                          title={omission.eventRef ?? undefined}
                        >
                          {shortRef(omission.eventRef)}
                        </span>
                        <span
                          dir="ltr"
                          className="font-mono text-xs text-muted-foreground"
                        >
                          {omission.field ?? "field not named"}
                        </span>
                        <Badge tone="outline">withheld by policy</Badge>
                      </li>
                    ))}
                  </ul>
                </details>
              </>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Runtime identity</CardTitle>
        </CardHeader>
        <CardBody>
          <dl className="space-y-2">
            {o.identity.map((axis) => (
              <div key={axis.axis}>
                <dt className="text-xs font-medium text-muted-foreground">
                  {axis.label}
                </dt>
                <dd
                  dir="ltr"
                  className={
                    axis.value !== null && axis.attested
                      ? "break-all font-mono text-xs text-foreground"
                      : "break-all font-mono text-xs text-muted-foreground"
                  }
                >
                  {axis.value === null
                    ? "not carried"
                    : axis.attested
                      ? axis.value
                      : "not attested"}
                </dd>
              </div>
            ))}
          </dl>
        </CardBody>
      </Card>
    </section>
  );
}
